import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { chatAPI } from './api';

const ChatRequestsContext = createContext(null);

export const useChatRequests = () => {
  const context = useContext(ChatRequestsContext);
  if (!context) {
    throw new Error('useChatRequests must be used within ChatRequestsProvider');
  }
  return context;
};

export const ChatRequestsProvider = ({ children }) => {
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);

  useEffect(() => {
    const onLogout = () => {
      setIncomingRequests([]);
      setOutgoingRequests([]);
    };
    window.addEventListener('userLoggedOut', onLogout);
    return () => window.removeEventListener('userLoggedOut', onLogout);
  }, []);

  const loadIncomingRequests = useCallback(async () => {
    try {
      const response = await chatAPI.getChatRequests();
      if (response.data.success) {
        setIncomingRequests(response.data.requests || []);
      }
    } catch (error) {
      console.error('Failed to load incoming requests:', error);
      setIncomingRequests([]);
    }
  }, []);

  const loadOutgoingRequests = useCallback(async () => {
    try {
      const response = await chatAPI.getOutgoingChatRequests();
      if (response.data.success) {
        setOutgoingRequests(response.data.requests || []);
      }
    } catch (error) {
      console.error('Failed to load outgoing requests:', error);
      setOutgoingRequests([]);
    }
  }, []);

  const removeIncomingRequest = useCallback((fromUserConnectId) => {
    setIncomingRequests((prev) => (
      prev.filter((req) => req.from_user_connect_id !== fromUserConnectId)
    ));
  }, []);

  const incomingCount = incomingRequests.length;

  const value = useMemo(() => ({
    incomingRequests,
    outgoingRequests,
    incomingCount,
    setIncomingRequests,
    setOutgoingRequests,
    loadIncomingRequests,
    loadOutgoingRequests,
    removeIncomingRequest,
  }), [
    incomingRequests,
    outgoingRequests,
    incomingCount,
    loadIncomingRequests,
    loadOutgoingRequests,
    removeIncomingRequest,
  ]);

  return (
    <ChatRequestsContext.Provider value={value}>
      {children}
    </ChatRequestsContext.Provider>
  );
};
